import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Star, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { T } from "@/context/TextEditContext";

function StarRow({ n, size = "w-4 h-4" }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${
            i <= n ? "text-brand-yellow fill-brand-yellow" : "text-stone-300"
          }`}
        />
      ))}
    </div>
  );
}

function ReviewForm({ onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [name, setName] = useState(
    () =>
      (typeof window !== "undefined"
        ? localStorage.getItem("bk_loyalty_name")
        : "") || ""
  );
  const [phone, setPhone] = useState(
    () =>
      (typeof window !== "undefined"
        ? localStorage.getItem("bk_loyalty_phone")
        : "") || ""
  );
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [hover, setHover] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please tap a star to rate us");
    if (!name.trim()) return toast.error("Please tell us your name");
    setSaving(true);
    try {
      await api.post("/reviews", { name, rating, text, phone });
      toast.success("Thanks for the review!");
      setRating(0);
      setText("");
      onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="review-form"
      className="bg-white border-2 border-stone-200 rounded-xl p-4 space-y-4"
    >
      <div className="text-center">
        <div className="font-hand text-3xl text-brand-red leading-none">
          Leave a review
        </div>
        <div className="text-xs text-stone-500 mt-1">
          Tell us how we did. It really helps.
        </div>
      </div>

      <div
        className="flex items-center justify-center gap-1"
        onMouseLeave={() => setHover(0)}
      >
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => setRating(i)}
            onMouseEnter={() => setHover(i)}
            data-testid={`review-star-${i}`}
            className="p-1 active:scale-90 transition-transform"
            aria-label={`${i} star${i === 1 ? "" : "s"}`}
          >
            <Star
              className={`w-9 h-9 ${
                i <= (hover || rating)
                  ? "text-brand-yellow fill-brand-yellow"
                  : "text-stone-300"
              }`}
            />
          </button>
        ))}
      </div>

      <input
        type="text"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="review-name-input"
        className="w-full px-4 py-2.5 rounded-lg border-2 border-stone-300 outline-none focus:border-brand-red text-sm"
      />
      <textarea
        placeholder="What was your favorite? What could we do better? (optional)"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 1000))}
        data-testid="review-text-input"
        rows={3}
        className="w-full px-4 py-2.5 rounded-lg border-2 border-stone-300 outline-none focus:border-brand-red text-sm resize-none"
      />
      <button
        type="submit"
        disabled={saving || !rating}
        data-testid="review-submit"
        className="w-full bg-brand-red text-white py-3 rounded-full font-black uppercase tracking-widest text-sm hover:bg-red-800 disabled:opacity-40 active:scale-[0.98] transition-all"
      >
        {saving ? "Sending..." : <T k="reviews.submit_btn" d="Post Review" />}
      </button>
    </form>
  );
}

function ReviewList({ reviews }) {
  if (!reviews.length) {
    return (
      <div className="text-center py-8 text-stone-400 font-hand text-2xl">
        Be the first to review us
      </div>
    );
  }
  return (
    <ul className="space-y-3" data-testid="review-list">
      {reviews.map((r) => (
        <li
          key={r.id}
          data-testid={`review-item-${r.id}`}
          className="bg-white border border-stone-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="font-bold text-stone-800">{r.name}</div>
            <StarRow n={r.rating} />
          </div>
          {r.text && (
            <p className="text-sm text-stone-600 mt-2 leading-snug whitespace-pre-line">
              {r.text}
            </p>
          )}
          <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-2" data-testid={`review-date-${r.id}`}>
            {new Date(r.created_at).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>
          {r.reply && (
            <div
              data-testid={`review-reply-${r.id}`}
              className="mt-3 ml-4 pl-3 border-l-4 border-brand-yellow bg-amber-50 rounded-r-lg py-2 pr-3"
            >
              <div className="text-[10px] uppercase tracking-widest font-black text-brand-red">
                <T k="reviews.owner_label" d="Blossom's Kitchen · Owner" />
              </div>
              <p className="text-sm text-stone-700 mt-1 leading-snug whitespace-pre-line">
                {r.reply}
              </p>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function ReviewsPage() {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/reviews");
      setData(res.data);
    } catch {
      /* silent — page still works */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen bg-stone-100 flex justify-center">
      <div className="w-full max-w-md lg:max-w-2xl min-h-screen bg-[#FDFBF7] phone-shell rustic-paper">
        <div className="chalkboard-band px-4 py-3 flex items-center gap-2 border-b-4 border-brand-red">
          <Link
            to="/"
            className="p-1 rounded-full hover:bg-white/10"
            data-testid="reviews-back-home"
          >
            <ChevronLeft className="w-5 h-5 text-brand-yellow" />
          </Link>
          <div className="font-display text-brand-yellow text-xl flex items-center gap-2">
            <MessageSquare className="w-5 h-5" /> <T k="reviews.title" d="Reviews" />
          </div>
        </div>

        <div className="p-4 space-y-4">
          <section
            data-testid="reviews-summary"
            className="bg-brand-red text-white rounded-2xl p-5 text-center"
          >
            {data.count > 0 ? (
              <>
                <div className="font-display text-brand-yellow text-6xl leading-none">
                  {data.average.toFixed(1)}
                </div>
                <div className="mt-2 flex justify-center">
                  <StarRow n={Math.round(data.average)} size="w-5 h-5" />
                </div>
                <div className="text-xs uppercase tracking-widest text-white/85 mt-2">
                  Based on {data.count} review{data.count === 1 ? "" : "s"}
                </div>
              </>
            ) : (
              <div className="font-hand text-brand-yellow text-4xl">
                No reviews yet
              </div>
            )}
          </section>

          <ReviewForm onSubmitted={load} />

          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black pt-2">
            <T k="reviews.list_label" d="What people are saying" />
          </div>

          {loading ? (
            <div className="text-center py-6 text-stone-400 font-hand text-xl">
              Loading...
            </div>
          ) : (
            <ReviewList reviews={data.reviews} />
          )}
        </div>
      </div>
    </div>
  );
}
